import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ImageUp, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PageHeader, StatusBadge, CandidateAvatar } from "@/components/Common";
import { CandidateForm } from "@/pages/admin/CandidateForm";
import { api, formatErr, useFetch } from "@/lib/api";

export default function AdminCandidates() {
  const { data, setData } = useFetch("/candidates");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const fileRef = useRef(null);
  const [photoFor, setPhotoFor] = useState(null);

  const upsert = (c) => setData((prev) => (prev.some((x) => x.id === c.id) ? prev.map((x) => (x.id === c.id ? c : x)) : [c, ...prev]));
  const open = (c) => { setEditing(c); setFormOpen(true); };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !photoFor) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: c } = await api.post(`/admin/candidates/${photoFor}/photo`, fd);
      upsert(c);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const del = async () => {
    try {
      await api.delete(`/admin/candidates/${toDelete.id}`);
      setData((prev) => prev.filter((x) => x.id !== toDelete.id));
      toast.success("Candidate removed");
    } catch (err) { toast.error(formatErr(err)); }
    setToDelete(null);
  };

  const shown = (data || []).filter((c) => `${c.name} ${c.title} ${c.category}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader eyebrow="Talent pool" title="Candidates" desc="Add, edit and publish candidate profiles for your members.">
        <Button data-testid="add-candidate-button" onClick={() => open(null)}><Plus className="mr-1.5 h-4 w-4" />Add candidate</Button>
      </PageHeader>
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input data-testid="admin-candidate-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search candidates" className="bg-white pl-9" />
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="candidate-photo-input" />
      <div className="overflow-x-auto rounded-md border border-stone-200 bg-white">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-stone-50 text-left text-[11px] font-mono uppercase tracking-wider text-stone-500">
            <tr><th className="px-5 py-3">Candidate</th><th className="px-5 py-3">Category</th><th className="px-5 py-3">Rate</th><th className="px-5 py-3">Exp.</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {shown.map((c) => (
              <tr key={c.id} data-testid={`admin-candidate-row-${c.id}`} className="hover:bg-stone-50/60">
                <td className="px-5 py-3">
                  <Link to={`/admin/candidates/${c.id}`} className="flex items-center gap-3">
                    <CandidateAvatar candidate={c} className="h-10 w-10" />
                    <span><span className="block font-medium text-stone-900 hover:underline">{c.name}</span><span className="text-xs text-stone-500">{c.title}</span></span>
                  </Link>
                </td>
                <td className="px-5 py-3 text-stone-600">{c.category}</td>
                <td className="px-5 py-3 font-mono">${c.hourly_rate}/hr</td>
                <td className="px-5 py-3 font-mono">{c.experience_years}y</td>
                <td className="px-5 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" title="Upload photo" data-testid={`upload-photo-${c.id}`} onClick={() => { setPhotoFor(c.id); fileRef.current?.click(); }}><ImageUp className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" title="Edit" data-testid={`edit-candidate-${c.id}`} onClick={() => open(c)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" title="Delete" data-testid={`delete-candidate-${c.id}`} onClick={() => setToDelete(c)} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CandidateForm open={formOpen} onOpenChange={setFormOpen} candidate={editing} onSaved={upsert} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove {toDelete?.name}?</AlertDialogTitle><AlertDialogDescription>This removes the profile from the member directory.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-candidate" onClick={del}>Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
